// @flow
import constant from 'lodash/fp/constant';
import groupBy from 'lodash/fp/groupBy';
import maxBy from 'lodash/fp/maxBy';
import orderBy from 'lodash/fp/orderBy';
import pipe from 'lodash/fp/pipe';
import invariant from 'invariant';

import {
  farFuture,
  toDate,
  today,
  latest,
  earliest,
  withDayOffset,
} from './date';
import { coerceIntoCustomCatalogAsCatalog } from './custom-catalog';
import { stringSearcher } from './search';
import { anyMatch } from './permissions';
import { getField } from './model';
import type {
  Catalog,
  CatalogPermissionHash,
  ResolvedCatalogAtsType,
} from './catalog';
import type { CustomCatalog, CustomCatalogAsCatalog } from './custom-catalog';
import { isLive } from './catalog-helper';

import { type TagTree, mergedListOfTagTrees, paths } from 'modules/tag-tree';
import type { CatalogCardLogos } from 'ui/settings-context';
import { type Index } from 'modules/search-engine';
import { entries } from 'modules/object';

export type AbstractCatalogFilter =
  | 'all'
  | 'standard_catalogs'
  | 'custom_catalogs';
export type AbstractCatalog = Catalog | CustomCatalogAsCatalog;
export type AbstractCatalogTuple = [string, AbstractCatalog[]];
export type BrandsIncludeBrand = (
  catalogBrands?: string[],
  brand?: string,
) => boolean;

export type SearchableAbstractCatalog = $ReadOnly<{|
  id: string,
  name: string,
  tagValues: $ReadOnlyArray<string>,
  original: AbstractCatalog,
|}>;

export const INDICES: $ReadOnlyArray<Index> = [
  { type: 'Field', property: ['name'] },
];

const byQuery = stringSearcher((c) => [c.name, c.key]);

export const isCustomCatalog = (
  abstractCatalog: AbstractCatalog,
): boolean %checks => !!abstractCatalog.isCustomCatalog;

export const isCatalog = (abstractCatalog: AbstractCatalog): boolean %checks =>
  !abstractCatalog.isCustomCatalog;

export const byFilters = (
  catalogs: AbstractCatalog[],
  filterType: AbstractCatalogFilter,
  query: string,
): AbstractCatalog[] =>
  (query.trim().length > 0 ? byQuery(query, catalogs) : catalogs).filter(
    {
      all: constant(true),
      standard_catalogs: isCatalog,
      custom_catalogs: isCustomCatalog,
    }[filterType],
  );

export const byClassification = (
  catalogs: $ReadOnlyArray<AbstractCatalog>,
): $ReadOnlyArray<AbstractCatalogTuple> =>
  pipe(
    groupBy('classification'),
    entries,
    orderBy([([_title, cats]) => maxBy('position', cats)?.position], ['desc']),
  )(catalogs);

const byPosition = ({
  position = 0,
}: AbstractCatalog | Catalog | CustomCatalogAsCatalog) => position;

const byUpdatedAt = (c: AbstractCatalog) =>
  toDate(c.updatedAt || farFuture).valueOf();

const defaultCatalogSort = pipe(
  orderBy([byPosition, byUpdatedAt], ['desc', 'desc']),
);

export const byDefault = (
  catalogs: $ReadOnlyArray<AbstractCatalog>,
): $ReadOnlyArray<AbstractCatalog> => defaultCatalogSort(catalogs);

export const all = (
  catalogs: $ReadOnlyArray<Catalog>,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
): AbstractCatalog[] =>
  defaultCatalogSort([
    ...(catalogs || []),
    ...(customCatalogs
      ? customCatalogs.map((cc) =>
          coerceIntoCustomCatalogAsCatalog(cc, catalogs),
        )
      : []
    ).filter(Boolean),
  ]);

export const withoutCatalogs = (
  catalogs: $ReadOnlyArray<Catalog>,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
): AbstractCatalog[] => [
  ...(customCatalogs
    ? customCatalogs.map((cc) => coerceIntoCustomCatalogAsCatalog(cc, catalogs))
    : []
  ).filter(Boolean),
];

export const firstCustomCatalogOrCatalog = (
  catalogs: $ReadOnlyArray<Catalog>,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
): AbstractCatalog =>
  byFilters(all([], customCatalogs), 'custom_catalogs', '')[0] ||
  byDefault(catalogs)[0];

export const byLive = (
  catalogs: $ReadOnlyArray<AbstractCatalog>,
): $ReadOnlyArray<AbstractCatalog> => catalogs.filter(isLive);

export const allLive = (
  catalogs: $ReadOnlyArray<Catalog>,
  customCatalogs: $ReadOnlyArray<CustomCatalog>,
  excludeCatalogs: boolean = false,
): $ReadOnlyArray<AbstractCatalog> => {
  const allCatalogs = excludeCatalogs
    ? withoutCatalogs(catalogs, customCatalogs)
    : all(catalogs, customCatalogs);
  return byLive(allCatalogs);
};

export const matchesPermissionsForKey: (
  key: string,
  permissionHash?: CatalogPermissionHash,
) => (AbstractCatalog) => boolean =
  (key, permissionHash = { ...null }) =>
  ({ permissions }): boolean =>
    anyMatch(permissionHash[key], permissions);

export const byPermissions = (
  catalogs: $ReadOnlyArray<AbstractCatalog>,
  number: string,
  catalogPermissions?: CatalogPermissionHash,
): AbstractCatalog[] =>
  catalogs.filter(matchesPermissionsForKey(number, catalogPermissions));

export const byKey = (
  haystack: $ReadOnlyArray<AbstractCatalog>,
  needle: string,
): AbstractCatalog | null => haystack.find((c) => c.key === needle) || null;

export const tile = ({ assets }: AbstractCatalog): string | null =>
  assets?.catalog_tiles?.[0]?.url || null;

export const clientFields = (
  catalog: AbstractCatalog,
): { [key: string]: any } => getField(catalog, 'clientFields') || { ...null };

const clientValue = (catalog: AbstractCatalog): string =>
  getField(clientFields(catalog), 'client') || '';

export const normalizedBrand = (brand: string): string =>
  brand.toLowerCase().replace(/\s+/g, '');

export const brandKey = (catalog: AbstractCatalog): string =>
  normalizedBrand(clientValue(catalog))
    ? normalizedBrand(clientValue(catalog))
    : '';

const defaultBrandLogoPath = ({
  brandLogoPath = 'clients/{client}/images/{brand}.png',
  client,
  brand,
}: {
  brandLogoPath: string,
  client: string,
  brand: string,
}): string =>
  brandLogoPath
    .replace('{client}', client)
    .replace('{brand}', normalizedBrand(brand));

export const catalogCardLogo = ({
  catalog,
  client,
  brandLogoPath,
  catalogCardLogos,
  clientLogo,
  brand = brandKey(catalog),
}: {
  catalog: AbstractCatalog,
  client: string,
  brandLogoPath: string,
  catalogCardLogos: CatalogCardLogos,
  clientLogo: string,
  brand?: string,
}): string =>
  brand
    ? catalogCardLogos[brand] ||
      defaultBrandLogoPath({ brandLogoPath, client, brand })
    : clientLogo;

export const brandsIncludeBrand: BrandsIncludeBrand = (
  catalogBrands = [],
  brand = '',
) =>
  catalogBrands.some(
    (catalogBrand) => normalizedBrand(brand) === normalizedBrand(catalogBrand),
  );

export const firstLiveDate = ({ dates }: AbstractCatalog): Date | null =>
  dates.first_live ? toDate(dates.first_live) : null;

export const lastLiveDate = ({ dates }: AbstractCatalog): Date | null =>
  dates.last_live ? toDate(dates.last_live) : null;

export const firstShipDate = ({
  dates = { ...null },
}: AbstractCatalog): Date | null =>
  dates.first_ship ? toDate(dates.first_ship) : null;

export const lastShipDate = ({ dates }: AbstractCatalog): Date | null =>
  dates.last_ship ? toDate(dates.last_ship) : null;

export const arriveOnMinimumDaysAfterFirstPossible = ({
  dates,
}: AbstractCatalog): number => Number(dates.ship_min_days) || 0;

export const arriveOnMaximumDays = ({
  dates,
}: AbstractCatalog): number | null => Number(dates.ship_max_days) || null;

export const defaultArriveOnOffsetDays = ({ dates }: AbstractCatalog): number =>
  Number(dates.ship_default_days) || 0;

export const latestOfFirstShipOrToday = (catalog: AbstractCatalog): Date => {
  const firstPossible = latest(
    [firstShipDate(catalog), today()].filter(Boolean),
  );

  invariant(
    firstPossible,
    'today() should be a Date (this should never happen)',
  );

  return firstPossible;
};

export const minimumArriveOn = (catalog: AbstractCatalog): Date =>
  withDayOffset(
    arriveOnMinimumDaysAfterFirstPossible(catalog),
    latestOfFirstShipOrToday(catalog),
  );

export const maximumArriveOn = (catalog: AbstractCatalog): Date => {
  const maxDays = arriveOnMaximumDays(catalog);
  const firstPossible = latestOfFirstShipOrToday(catalog);

  const lastPossible = earliest(
    [
      maxDays === null ? null : withDayOffset(maxDays, firstPossible),
      lastShipDate(catalog),
      farFuture,
    ].filter(Boolean),
  );

  invariant(
    lastPossible,
    'farFuture should be a Date (this should never happen)',
  );

  return lastPossible;
};

export const defaultArriveOn = (catalog: AbstractCatalog): Date => {
  const resolved = earliest(
    [
      latest(
        [
          minimumArriveOn(catalog),
          withDayOffset(defaultArriveOnOffsetDays(catalog), today()),
        ].filter(Boolean),
      ),
      maximumArriveOn(catalog),
    ].filter(Boolean),
  );

  invariant(
    resolved,
    'all these things should be dates (this should never happen)',
  );

  return resolved;
};

export const defaultProductReleaseDate = ({
  dates = { ...null },
}: AbstractCatalog): Date | null =>
  dates.first_ship ? toDate(dates.first_ship) : null;

export const dealerAtsLabelMax = (catalog: AbstractCatalog): number | null =>
  typeof catalog.ats_label_max === 'number' ? catalog.ats_label_max : null;

export const repAtsLabelMax = (catalog: AbstractCatalog): number | null =>
  typeof catalog.ats_label_max_rep === 'number'
    ? catalog.ats_label_max_rep
    : null;

export const stockItemCompoundKey = (
  catalog: AbstractCatalog,
  stockItemKey: string,
): string => `catalog:${catalog.key}:stockItem:${stockItemKey}`;

export const tagTree = (catalog: AbstractCatalog): TagTree => {
  const catalogTypeTag = isCatalog(catalog) ? 'Standard' : 'Custom';
  // check if the client is an enterprise client or a digital market client
  // the evidence of an elastic_instance_url on the catalog indicates an enterprise client.
  const catalogBrandTag = catalog.elastic_instance_url
    ? 'Elastic Enterprise'
    : 'Digital Market';
  return mergedListOfTagTrees([
    catalog.tags || { ...null },
    { 'Catalog Type': [catalogTypeTag] },
    { 'Brand Type': [catalogBrandTag] },
  ]);
};

export const asSearchable = (
  catalog: AbstractCatalog,
): SearchableAbstractCatalog => ({
  // eslint-disable-next-line no-underscore-dangle
  id: catalog._id,
  name: catalog.name,
  tagValues: paths(tagTree(catalog)).flat(),
  original: catalog,
});

export const inNameOrder = (
  list: $ReadOnlyArray<AbstractCatalog>,
): $ReadOnlyArray<AbstractCatalog> => orderBy([(c) => c.name])(['asc'])(list);

/**
 * We only want to show share and download buttons if
 * automatic pdf generation for catalogs is turned on
 * for the given client.
 * Automatic pdf generation, as of today,
 * has only been implemented for TNF,
 * so printed_catalog data is only available for tnf atm.
 * The automated print catalog generation creates
 * print catalogs when importing the client's catalog data
 */
export const hasPrintedCatalog = (row: AbstractCatalog): boolean =>
  !!row.printed_catalog;

/**
 * Only allow the sharing or downloading
 * of the automated print catalog after it
 * has been completed
 */
export const hasReadyPrintedCatalog = (catalog: AbstractCatalog): boolean =>
  catalog.state === 'completed';

export const isSharedWithMe: (AbstractCatalog) => boolean = (catalog) =>
  Boolean(catalog.sharedWithMeBy);
export const isSharedWithOthers: (AbstractCatalog) => boolean = (catalog) =>
  Boolean(catalog.totalCopies);

type CatalogAtsType = (
  shipmentsByAvailableStock: boolean,
) => (catalog: AbstractCatalog) => ResolvedCatalogAtsType;

export const catalogAtsType: CatalogAtsType =
  (shipmentsByAvailableStock) => (catalog) =>
    catalog.type === 'blocking' && shipmentsByAvailableStock
      ? 'shipmentsByAvailableStock'
      : catalog.type;

type CatalogAtsMode = (
  usesProductLevelAts: boolean,
  ShipmentsByAvailableStockStrategy: boolean,
) => (catalog: AbstractCatalog) => ResolvedCatalogAtsType | null;

export const catalogAtsMode: CatalogAtsMode =
  (usesProductLevelAts, shipmentsByAvailableStock) => (catalog) =>
    // eslint-disable-next-line no-nested-ternary
    usesProductLevelAts
      ? null
      : catalogAtsType(shipmentsByAvailableStock)(catalog);
